import { FormEvent, useState } from 'react'
import { settingsApi, type ProviderListItem } from '@/lib/api/settings'
import { Button } from '@/components/ui/button'

interface ProviderApiKeysPanelProps {
  providers: ProviderListItem[]
  onProvidersChange: (providers: ProviderListItem[]) => void
  busy: boolean
  setBusy: (busy: boolean) => void
  setMessage: (message: string | null) => void
}

export function ProviderApiKeysPanel({
  providers,
  onProvidersChange,
  busy,
  setBusy,
  setMessage,
}: ProviderApiKeysPanelProps) {
  const [draftKeys, setDraftKeys] = useState<Record<string, string>>({})

  const refreshProviders = async () => {
    const items = await settingsApi.listProviders()
    onProvidersChange(items)
  }

  const handleSave = async (event: FormEvent, slug: string) => {
    event.preventDefault()
    const apiKey = draftKeys[slug]?.trim()
    if (!apiKey) return

    setBusy(true)
    setMessage(null)
    try {
      await settingsApi.putApiKey(slug, apiKey)
      setDraftKeys((current) => ({ ...current, [slug]: '' }))
      await refreshProviders()
      setMessage(`${slug} API key saved.`)
    } catch {
      setMessage(`Failed to save ${slug} API key.`)
    } finally {
      setBusy(false)
    }
  }

  const handleTest = async (slug: string) => {
    setBusy(true)
    setMessage(null)
    try {
      const result = await settingsApi.testApiKey(slug)
      setMessage(
        result.success
          ? `${slug}: connection successful.`
          : `${slug}: connection failed — ${result.error ?? 'unknown'}`,
      )
      await refreshProviders()
    } catch {
      setMessage(`${slug}: connection test failed.`)
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (slug: string) => {
    setBusy(true)
    setMessage(null)
    try {
      await settingsApi.deleteApiKey(slug)
      await refreshProviders()
      setMessage(`${slug} API key removed.`)
    } catch {
      setMessage(`Failed to remove ${slug} API key.`)
    } finally {
      setBusy(false)
    }
  }

  if (providers.length === 0) {
    return (
      <section className="rounded-lg border border-border bg-card p-4" data-testid="settings-provider-keys">
        <h2 className="text-sm font-semibold">Provider API Keys</h2>
        <p className="mt-2 text-sm text-muted-foreground">No providers configured.</p>
      </section>
    )
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4 md:col-span-2" data-testid="settings-provider-keys">
      <h2 className="text-sm font-semibold">Provider API Keys</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Configure keys for OpenRouter, Groq, and other enabled providers.
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {providers.map((provider) => (
          <div
            key={provider.slug}
            className="rounded-md border border-border p-3"
            data-testid={`settings-provider-${provider.slug}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-medium">{provider.name}</h3>
                <p className="text-xs text-muted-foreground">{provider.slug}</p>
              </div>
              {provider.keyStatus ? (
                <span className="text-xs text-muted-foreground">{provider.keyStatus}</span>
              ) : null}
            </div>

            {provider.keyHint ? (
              <p className="mt-2 text-sm text-muted-foreground" data-testid={`settings-api-key-hint-${provider.slug}`}>
                Current key: {provider.keyHint}
              </p>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">No API key configured.</p>
            )}

            <form onSubmit={(event) => void handleSave(event, provider.slug)} className="mt-3 space-y-2">
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">API key</span>
                <input
                  type="password"
                  value={draftKeys[provider.slug] ?? ''}
                  onChange={(event) =>
                    setDraftKeys((current) => ({ ...current, [provider.slug]: event.target.value }))
                  }
                  className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2"
                  data-testid={`settings-api-key-input-${provider.slug}`}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={busy || !(draftKeys[provider.slug]?.trim())}
                >
                  Save key
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy || !provider.keyHint}
                  onClick={() => void handleTest(provider.slug)}
                  data-testid={`settings-test-connection-${provider.slug}`}
                >
                  Test connection
                </Button>
                {provider.keyHint ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void handleDelete(provider.slug)}
                    data-testid={`settings-delete-key-${provider.slug}`}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            </form>
          </div>
        ))}
      </div>
    </section>
  )
}
